// WHAT: Tab component for displaying all aspects across workspaces
// WHY:  Provides overview of all configured aspects with their workspaces and colors
// DOMAIN: Productivity → TimeTracQ
// DEPENDENCIES: useWorkspaceConfig hook
// RISKS:
//   - NOTE: Meta key filtering is hardcoded - may miss other meta keys
//   - NOTE: Color extraction may fail if aspect data structure is unexpected
//   - NOTE: No error handling for malformed config

import { useState, useEffect } from "react";

import { Card } from "@/components/ui";

import { useWorkspaceConfig } from "../../hooks/time_tracking/useWorkspaceConfig";

/**
 * Aspect information interface
 */
interface AspectInfo {
  /** Display color */
  color: string;
  /** Aspect name */
  name: string;
  /** Workspace name */
  workspace: string;
}

/**
 * WHAT:
 *   Tab component that displays all aspects across all workspaces with their
 *   colors and workspace associations.
 *
 * WHY:
 *   Enables users to see all configured aspects in one place for reference
 *   and management.
 *
 * BEHAVIOR:
 *   - Loads workspace configuration
 *   - Extracts all aspects from all workspaces
 *   - Filters out meta keys
 *   - Displays aspects with workspace and color information
 *   - Updates when config changes
 *
 * RISKS:
 *   - NOTE: Meta key filtering is hardcoded - may miss other meta keys
 *   - NOTE: Color extraction may fail if aspect data structure is unexpected
 *   - NOTE: No error handling for malformed config
 *
 * @returns JSX.Element
 *
 */
export const AspectsTab = () => {
  const [config, , isLoading] = useWorkspaceConfig();
  const [aspects, setAspects] = useState<AspectInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    try {
      if (Object.keys(config).length === 0) {
        setAspects([]);
        return;
      }

      const aspectList: AspectInfo[] = [];

      // NOTE: Iterate through all workspaces and their aspects
      for (const [workspaceName, workspaceData] of Object.entries(config)) {
        // NOTE: Filter out meta keys
        const metaKeys = ["settings", "Meta", "System", "Version"];
        if (metaKeys.includes(workspaceName)) {
          continue;
        }

        // NOTE: Extract aspects
        // The WorkspaceConfig is directly an object with Aspects as keys
        if (typeof workspaceData === "object") {
          for (const [aspectName, aspectData] of Object.entries(workspaceData)) {
            const color = (aspectData as undefined | { color?: string })?.color ?? "#6B7280";
            aspectList.push({
              color: color,
              name: aspectName,
              workspace: workspaceName,
            });
          }
        }
      }

      setAspects(aspectList);
    } catch {
      setError("Fehler beim Laden der Aspekte");
    } finally {
      setLoading(false);
    }
  }, [config]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card
        className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
        padding="lg"
        variant="elevated"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-tracq-deep-orange/20 flex items-center justify-center">
            <svg
              className="w-6 h-6 text-tracq-deep-orange"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
          </div>
          <div>
            <h3 className="text-2xl font-bold text-white">Aspects</h3>
            <p className="text-slate-400 text-sm">Alle verfügbaren Aspekte und deren Workspaces</p>
          </div>
        </div>
      </Card>

      {/* Aspects Liste */}
      <Card
        className="bg-slate-700/30 border-slate-600/50 shadow-tracq-card"
        padding="lg"
        variant="elevated"
      >
        {(() => {
          if (loading || isLoading) {
            return (
              <div className="h-40 flex items-center justify-center text-slate-500 animate-pulse">
                Loading…
              </div>
            );
          }
          if (error) {
            return (
              <div className="h-40 flex items-center justify-center text-red-400">{error}</div>
            );
          }
          if (aspects.length === 0) {
            return (
              <div className="h-40 flex items-center justify-center text-slate-300">
                Keine Aspekte konfiguriert
              </div>
            );
          }
          return (
            <div className="space-y-4">
              {aspects.map((aspect, index) => (
                <div
                  className="flex items-center gap-4 p-4 rounded-xl bg-slate-800/30 border border-slate-600/50 hover:bg-slate-800/50 transition-all duration-200"
                  key={`${aspect.workspace}-${aspect.name}`}
                >
                  {/* Aspect Info */}
                  <div className="flex items-center gap-4 flex-1">
                    <div
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: aspect.color }}
                    />
                    <div>
                      <div className="text-lg font-semibold text-white">
                        {index + 1}. {aspect.name}
                      </div>
                      <div className="text-sm text-slate-400">Workspace: {aspect.workspace}</div>
                    </div>
                  </div>

                  {/* Workspace Badge */}
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 rounded-lg text-xs font-medium bg-slate-600/50 text-slate-300 border border-slate-500/50">
                      {aspect.workspace}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </Card>
    </div>
  );
};
